import React, { useState, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { getParsedQueryString } from '@Utils/QueryString';
import { setLoggedIn, removeAllUserState, removeUserInLocalStorage } from '@Services/User';
import { getIs2033User, getAuthor } from '@Services/User/Request';

import NoticeNot2033UserModal from '@Components/Login/NoticeNot2033UserModal';
// import NoticeLowLevelUserModal from '@Components/Login/NoticeLowLevelUserModal';

import MainBackgroundImage from '@Assets/Images/bg_main.jpg';
import LogoImage from '@Assets/Images/logo_workshop.png';
import GoogleLoginButtonNormalImage from '@Assets/Images/btn_google_signin_dark_normal_web.png';
import GoogleLoginButtonPressedImage from '@Assets/Images/btn_google_signin_dark_pressed_web.png';

const _WEB_CLIENT_ID = '538902241862-kam31j8hen9kgkh7ie2bgtng82nn2gop.apps.googleusercontent.com';

const AUTHOR_STATUS_CODE = {
  CREATE_AUTHOR_NEEDED: 400,
};

const IS_2033_USER_REPONSE_MESSAGE = {
  NOT_EXIST: 'notexist',
  LOW_LEVEL: 'lowlevel',
  NOT_TESTER: 'Tester notexist',
  NOT_BETATESTER: 'BetaTester notexist',
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // console.log('Login');
  // const [isNoticeLowLevelUserModalVisible, setIsNoticeLowLevelUserModalVisible] = useState(false);
  const [isNoticeNot2033UserModalVisible, setIsNoticeNot2033UserModalVisible] = useState(false);
  const [isNoticeBetaTester, setIsNoticeBetaTester] = useState(false);

  // const handleIsNoticeLowLevelUserModalVisible = useCallback(() => {
  //   setIsNoticeLowLevelUserModalVisible(!isNoticeLowLevelUserModalVisible);
  // }, [isNoticeLowLevelUserModalVisible]);

  const dispatchRemoveAllUserState = useCallback(() => {
    dispatch(removeAllUserState());
  }, [dispatch]);

  const handleCloseModalClick = () => {
    dispatchRemoveAllUserState();
    removeUserInLocalStorage();
    setIsNoticeBetaTester(false);
    setIsNoticeNot2033UserModalVisible(false);
    navigate('/login', { replace: true });
  };

  const handleIsNoticeNot2033UserModalVisible = useCallback(() => {
    setIsNoticeNot2033UserModalVisible(!isNoticeNot2033UserModalVisible);
  }, [isNoticeNot2033UserModalVisible]);

  const updateLoggedIn = useCallback(
    accessToken => {
      dispatch(setLoggedIn({ isLoggedIn: true, accessToken }));
    },
    [dispatch],
  );

  const updateAuthor = useCallback(async () => {
    try {
      await getAuthor();
      navigate('/', { replace: true });
    } catch (error) {
      const { status } = error;

      if (status === AUTHOR_STATUS_CODE.CREATE_AUTHOR_NEEDED) {
        navigate('/nickname', { replace: true });
      }
    }
  }, [navigate]);

  const removeUserinfo = () => {
    dispatchRemoveAllUserState();
    removeUserInLocalStorage();
  };
  const checkIs2033User = async () => {
    const { msg } = await getIs2033User();

    if (msg === IS_2033_USER_REPONSE_MESSAGE.NOT_EXIST) {
      handleIsNoticeNot2033UserModalVisible();
      removeUserinfo();
      return;
    }

    await updateAuthor();
  };

  const handleLogin = useCallback(async () => {
    const { token: accessToken } = getParsedQueryString(window.location);

    if (accessToken) {
      updateLoggedIn(accessToken);
      checkIs2033User();
    }
  }, [dispatch]);

  const handleLoginButtonClick = async () => {
    const authUrl =
      'https://accounts.google.com/o/oauth2/v2/auth?' +
      `&client_id=${_WEB_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent('https://auth.banjihagames.com/oauth2')}` +
      '&response_type=code' +
      '&access_type=offline' +
      '&scope=profile%20email' +
      `&state=${encodeURIComponent(window.location.href)}`;

    window.location.href = authUrl;
  };

  useEffect(() => {
    handleLogin();
  }, [window.location]);
  return (
    <>
      <LoginContainer>
        <LogoContainer>
          <Logo />
        </LogoContainer>

        <LoginTextContainer>
          <Title>당신의 서울을 들려주세요</Title>
          <LoginButton type="button" onClick={handleLoginButtonClick} />
        </LoginTextContainer>
      </LoginContainer>

      <NoticeNot2033UserModal
        isNoticeNot2033UserModalVisible={isNoticeNot2033UserModalVisible}
        handleCloseModalClick={handleCloseModalClick}
        label="등록되지 않은 계정입니다."
      />

      {/* <NoticeLowLevelUserModal
        isNoticeLowLevelUserModalVisible={isNoticeLowLevelUserModalVisible}
        handleCloseModalClick={handleCloseModalClick}
        handleUsingEditorClick={updateAuthor}
        label="서울2033 레벨이 낮아 작품 프리뷰, 출시 등"
      /> */}

      <NoticeNot2033UserModal
        isNoticeNot2033UserModalVisible={isNoticeBetaTester}
        handleCloseModalClick={handleCloseModalClick}
        label="베타 테스터 유저가 아닙니다."
      />
    </>
  );
};

const FlexAlignCenterCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackgroundImageCSS = css`
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const LoginContainer = styled.div`
  ${FlexAlignCenterCSS}
  ${BackgroundImageCSS}

  height: 100vh;
  background-color: #323232;
  background-image: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0) 22.99%, rgba(0, 0, 0, 0.2) 100%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${MainBackgroundImage});
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 43px;
  left: 50px;
`;

const Logo = styled.img.attrs({
  src: LogoImage,
  alt: '서울 2033 창작마당',
})`
  width: 110px;
  height: 100%;
  -webkit-user-drag: none;

  margin-bottom: 32px;
`;

const LoginTextContainer = styled.div`
  ${FlexAlignCenterCSS}

  width: 100%;
  height: auto;
  flex-direction: column;
`;

const Title = styled.span`
  ${FlexAlignCenterCSS}

  font-size: min(7vw, 54px);
  color: ${({ theme }) => theme.colors.ui_1};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-align: center;
`;

const LoginButton = styled.button`
  ${BackgroundImageCSS}

  outline: none;
  border: none;
  width: 185px;
  height: 40px;
  padding: 0px;
  margin-top: 60px;

  background-color: transparent;
  background-image: url(${GoogleLoginButtonNormalImage});
  cursor: pointer;

  &:hover {
    background-image: url(${GoogleLoginButtonPressedImage});
  }
`;

export default Login;

// import React, { useState, useCallback, useEffect } from 'react';
// import styled, { css } from 'styled-components';
// import { useNavigate } from 'react-router-dom';
// import { useDispatch } from 'react-redux';

// import { getParsedQueryString } from '@Utils/QueryString';
// import { setLoggedIn, removeAllUserState, removeUserInLocalStorage } from '@Services/User';
// import { getIs2033User, getAuthor, isBetaTester } from '@Services/User/Request';

// import NoticeNot2033UserModal from '@Components/Login/NoticeNot2033UserModal';
// // import NoticeLowLevelUserModal from '@Components/Login/NoticeLowLevelUserModal';

// import MainBackgroundImage from '@Assets/Images/bg_main.jpg';
// import LogoImage from '@Assets/Images/logo_workshop.png';
// import GoogleLoginButtonNormalImage from '@Assets/Images/btn_google_signin_dark_normal_web.png';
// import GoogleLoginButtonPressedImage from '@Assets/Images/btn_google_signin_dark_pressed_web.png';

// const _WEB_CLIENT_ID = '538902241862-kam31j8hen9kgkh7ie2bgtng82nn2gop.apps.googleusercontent.com';

// const AUTHOR_STATUS_CODE = {
//   CREATE_AUTHOR_NEEDED: 400,
// };

// const IS_2033_USER_REPONSE_MESSAGE = {
//   NOT_EXIST: 'notexist',
//   LOW_LEVEL: 'lowlevel',
//   NOT_TESTER: 'Tester notexist',
//   NOT_BETATESTER: 'BetaTester notexist',
// };

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [isNoticeNot2033UserModalVisible, setIsNoticeNot2033UserModalVisible] = useState(false);
//   // const [isNoticeLowLevelUserModalVisible, setIsNoticeLowLevelUserModalVisible] = useState(false);

//   const [isNoticeBetaTester, setIsNoticeBetaTester] = useState(false);

//   const handleIsNoticeNot2033UserModalVisible = useCallback(() => {
//     setIsNoticeNot2033UserModalVisible(!isNoticeNot2033UserModalVisible);
//   }, [isNoticeNot2033UserModalVisible]);

//   // const handleIsNoticeLowLevelUserModalVisible = useCallback(() => {
//   //   setIsNoticeLowLevelUserModalVisible(!isNoticeLowLevelUserModalVisible);
//   // }, [isNoticeLowLevelUserModalVisible]);

//   const dispatchRemoveAllUserState = useCallback(() => {
//     dispatch(removeAllUserState());
//   }, [dispatch]);

//   const handleCloseModalClick = () => {
//     dispatchRemoveAllUserState();
//     removeUserInLocalStorage();
//     setIsNoticeBetaTester(false);

//     navigate('/login', { replace: true });
//   };

//   const updateLoggedIn = useCallback(
//     accessToken => {
//       dispatch(setLoggedIn({ isLoggedIn: true, accessToken }));
//     },
//     [dispatch],
//   );

//   const updateAuthor = useCallback(async () => {
//     try {
//       await getAuthor();
//       navigate('/', { replace: true });
//     } catch (error) {
//       const { status } = error;

//       if (status === AUTHOR_STATUS_CODE.CREATE_AUTHOR_NEEDED) {
//         navigate('/nickname', { replace: true });
//       }
//     }
//   }, [navigate]);

//   const removeUserinfo = () => {
//     dispatchRemoveAllUserState();
//     removeUserInLocalStorage();
//   };
//   const checkIs2033User = async () => {
//     const { msg } = await getIs2033User();

//     if (msg === IS_2033_USER_REPONSE_MESSAGE.NOT_EXIST) {
//       handleIsNoticeNot2033UserModalVisible();
//       removeUserinfo();
//       return;
//     }
//     // if (msg === IS_2033_USER_REPONSE_MESSAGE.LOW_LEVEL) {
//     //   handleIsNoticeLowLevelUserModalVisible();
//     //   removeUserinfo();
//     //   return;
//     // }

//     const betaTester = await isBetaTester();
//     if (betaTester.msg === IS_2033_USER_REPONSE_MESSAGE.NOT_BETATESTER) {
//       setIsNoticeBetaTester(!isNoticeBetaTester);
//       removeUserinfo();

//       return;
//     }

//     await updateAuthor();
//   };

//   const handleLogin = useCallback(() => {
//     const { token: accessToken } = getParsedQueryString(window.location);

//     if (accessToken) {
//       updateLoggedIn(accessToken);
//       checkIs2033User();
//     }
//   }, [dispatch]);

//   const handleLoginButtonClick = () => {
//     const authUrl =
//       'https://accounts.google.com/o/oauth2/v2/auth?' +
//       `&client_id=${_WEB_CLIENT_ID}` +
//       `&redirect_uri=${encodeURIComponent('https://auth.banjihagames.com/oauth2')}` +
//       '&response_type=code' +
//       '&access_type=offline' +
//       '&scope=profile%20email' +
//       `&state=${encodeURIComponent(window.location.href)}`;

//     window.location.href = authUrl;
//   };

//   useEffect(() => {
//     handleLogin();
//   }, [window.location]);
//   return (
//     <>
//       <LoginContainer>
//         <LogoContainer>
//           <Logo />
//         </LogoContainer>

//         <LoginTextContainer>
//           <Title>당신의 서울을 들려주세요</Title>
//           <LoginButton type="button" onClick={handleLoginButtonClick} />
//         </LoginTextContainer>
//       </LoginContainer>

//       <NoticeNot2033UserModal
//         isNoticeNot2033UserModalVisible={isNoticeNot2033UserModalVisible}
//         handleCloseModalClick={handleCloseModalClick}
//         label="등록되지 않은 계정입니다."
//       />

//       {/* <NoticeLowLevelUserModal
//         isNoticeLowLevelUserModalVisible={isNoticeLowLevelUserModalVisible}
//         handleCloseModalClick={handleCloseModalClick}
//         handleUsingEditorClick={updateAuthor}
//         label="서울2033 레벨이 낮아 작품 프리뷰, 출시 등"
//       /> */}

//       <NoticeNot2033UserModal
//         isNoticeNot2033UserModalVisible={isNoticeBetaTester}
//         handleCloseModalClick={handleCloseModalClick}
//         label="베타 테스터 유저가 아닙니다."
//       />
//     </>
//   );
// };

// const FlexAlignCenterCSS = css`
//   display: flex;
//   align-items: center;
//   justify-content: center;
// `;

// const BackgroundImageCSS = css`
//   background-size: cover;
//   background-position: center;
//   background-repeat: no-repeat;
// `;

// const LoginContainer = styled.div`
//   ${FlexAlignCenterCSS}
//   ${BackgroundImageCSS}

//   height: 100vh;
//   background-color: #323232;
//   background-image: radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0) 22.99%, rgba(0, 0, 0, 0.2) 100%),
//     linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${MainBackgroundImage});
// `;

// const LogoContainer = styled.div`
//   position: absolute;
//   top: 43px;
//   left: 50px;
// `;

// const Logo = styled.img.attrs({
//   src: LogoImage,
//   alt: '서울 2033 창작마당',
// })`
//   width: 110px;
//   height: 100%;
//   -webkit-user-drag: none;

//   margin-bottom: 32px;
// `;

// const LoginTextContainer = styled.div`
//   ${FlexAlignCenterCSS}

//   width: 100%;
//   height: auto;
//   flex-direction: column;
// `;

// const Title = styled.span`
//   ${FlexAlignCenterCSS}

//   font-size: min(7vw, 54px);
//   color: ${({ theme }) => theme.colors.ui_1};
//   font-weight: ${({ theme }) => theme.fontWeights.bold};
//   text-align: center;
// `;

// const LoginButton = styled.button`
//   ${BackgroundImageCSS}

//   outline: none;
//   border: none;
//   width: 185px;
//   height: 40px;
//   padding: 0px;
//   margin-top: 60px;

//   background-color: transparent;
//   background-image: url(${GoogleLoginButtonNormalImage});
//   cursor: pointer;

//   &:hover {
//     background-image: url(${GoogleLoginButtonPressedImage});
//   }
// `;

// export default Login;
